<template>
  <hr>

  <div>
    <ul class="nav nav-tabs">
      <li v-for="(tab, index) in availableTabs" :key="index" :class="{ active: activeTab === index }">
        <a href="#" class="tab-name" @click.prevent="activeTab = index"><b>{{ tab }}</b></a>
        <a href="#" class="edit-tab-name" @click.prevent="editChecklistName(index)"><font-awesome-icon :icon="['fas', 'pen']" /></a>
      
      </li>
      <li>
        <button class="btn btn-primary btn-xs" @click.prevent="addChecklist()">
        <font-awesome-icon :icon="['fas', 'circle-plus']" />
        New Checklist
      </button>
      </li>
    </ul>
    <DismissableMessage v-if="errorMessage" :message="errorMessage" :component-state="SaveStates.Errored" @close="resetMessage" />
    <div class="tab-content">
      <div class="tab-pane active">
        <DataTable
          ref="checklistItemsIndex"
          :key="datatableKey"
          show-gridlines
          edit-mode="cell"
          scrollHeight="70vh" 
          scrollable
          row-hover
          striped-rows
          :lazy="true"
          :value="checklistItems"
          :loading="isSavableStateProcessing"
          @table-row-select="selectedChecklistRows"
          v-model:selection="selectedRows"
          :row-class="(data) => data.rowClass">
          <template #empty>No templates available!</template>
          <template #header>
            <div class="d-flex justify-content-between w-100 align-items-center" v-if="isAdminVerified">  
              <strong>
                <span style="color: green;">Verified by Manager:</span> {{ selectedMatch.checklists[activeTab].verified_by.full_name }} ({{ selectedMatch.checklists[activeTab].verified_at.display }})
                <br><span> Notes:</span> {{ selectedMatch.checklists[activeTab].verification_notes }}
              </strong>
            </div>
            <div class="d-flex justify-content-between w-100 align-items-center">
              <div class="d-flex align-items-center gap-2">
                <div class="save-toolbar action-row">
                  <button href="#" title="Print"
                    class="action btn btn-secondary" @click.prevent="openPrintPreview">
                    Print
                </button>
                </div>
                <button class="btn btn-outline-primary" style="margin-top: 0.6rem" @click.prevent="enableBulkEdit">
                  <font-awesome-icon :icon="['fas', (bulkEdit ? 'edit' : 'pen')]" />
                  <span v-if="bulkEdit" style="padding-right: 0.5rem">Disable Select Mode</span>
                  <span v-else style="padding-right: 0.5rem">Enable Select Mode</span>
                </button>
                <button v-if="bulkEdit && selectedRows" class="btn btn-outline-primary" style="margin-top: 0.6rem" @click="clearSelectedRows" :disabled="selectedRows.length === 0">
                  <font-awesome-icon :icon="['fas', 'square-check']" />
                  <span style="padding-right: 0.5rem">Clear Selection</span>
                </button>
              </div>

              <div class="d-flex align-items-center">
                <MultiSelectInput
                  v-model="selectedTemplate"
                  name="templates"
                  label="Available Templates"
                  :options="checklistStore.checklistTemplatesAvailable"
                  label-key="template_name"
                  placeholder="Select Template"
                  required="true"
                  value-key="id"/>

                <ActionToolbar :component-state="currentState"
                  primary-button-text="Add Template"
                  @primary-click="addTemplate()"
                  :primary-disabled="isAdminVerified || !selectedTemplate"
                  :secondary-enabled="false"
                  :error-message="formError" />

                <div class="row" v-if="appStore.currentUser.hasPermission('internal_match_checklists_verify')">
                  <div class="save-toolbar action-row col-sm-12">
                    <a href="#" title="Verify" v-if="!isAdminVerified"
                      class="action btn btn-primary" @click.prevent="showModalFor(ActionModals.AOC_SIGNOFF)">
                      Manager Verify
                    </a>
                    <a href="#" title="Undo Verify" v-else
                      class="action btn btn-secondary" @click.prevent="undoSignOff">
                      Undo Manager Verify
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <Column selection-mode="multiple" 
            header-style="width: 3rem" 
            :frozen="true"
            column-key="bulk_edit"
            align-frozen="left"
            :reorderable-column="false"
            v-if="bulkEdit"/>
          <Column field="template_name" header="Template Name">
            <template #body="{ data }">
              {{ data.applied_template.template_name }}
              <a v-if="data.applied_template.reference_link" title="Reference" class="table-link" target="_blank" :href="data.applied_template.reference_link">
                <font-awesome-icon :icon="['fas', 'link']" fixed-width />
              </a>
            </template>
          </Column>
          <Column field="template_item.item_type.item_type" header="Item Type"/>
          <Column field="item_body_instance" header="Details" body-style="word-break: break-word;">
            <template #body="{ data }">
              <span class="preserve-spacing">
              {{ data.item_body_instance }}
              </span>
            </template>
            <template #editor="{ index }" v-if="!isAdminVerified">
              <TextArea 
                v-model="checklistItems[index].item_body_instance" 
                :autoResize="true" 
                rows="1" 
                @keydown.enter.stop 
                :disabled="isAdminVerified"
                class="w-full form-control" 
              />
            </template>
          </Column>
          <Column field="status" header="Status">
            <template #body="{ data }">
              <Select
                v-model="data.status"
                :options="statusOptions"
                optionValue="code"
                optionLabel="label"
                class="form-control"
                :disabled="isAdminVerified"
              />
              <template v-if="data.status_changed_at && data.updated_by_id">
                <hr class="inline-row">
                <strong>Changed by:</strong> {{ data.updated_by.full_name }} <br> ({{formatDateTimeET(data.status_changed_at)}} ET)
              </template>
            </template>
          </Column>

          <Column class="btn-3-icon-group" style="min-width:260px;">
            <template #body="slotProps">
              <div class="d-flex gap-2">
                <button class="action btn btn-success" :disabled="!slotProps.data.isDirty" @mousedown="onSave(slotProps.data)">
                  Save
                </button>
                <button class="action btn btn-outline-secondary" :disabled="!slotProps.data.isDirty" @mousedown="onCancel(slotProps)">
                  Cancel
                </button>
                <a href="#" title="Verify" class="action btn btn-primary" v-if="!isAdminVerified && hasCoordinatorSignOffEnabled" @mousedown="showModalFor(ActionModals.SIGNOFF, slotProps.data)">
                  Verify
                </a>
              </div>
              <template v-if="slotProps.data.signed_at && hasCoordinatorSignOffEnabled">
                <br>
                <span style="color: green;"><b>Verified by:</b></span> {{ slotProps.data.signed_by.full_name }} <br> ({{ formatDateTimeET(slotProps.data.signed_at) }} ET)
              </template>
            </template>
          </Column>
          <template #footer v-if="bulkEdit" >    
            <button v-if="selectedRows" :disabled="!selectedRows.length" class="btn btn-success mr-3" @click.prevent="showBulkUpdateModal()">
              Update Status
            </button>   
          </template>
        </DataTable>
      </div>
    </div>
  </div>
  <BulkUpdateModal ref="bulkUpdateModal"
    :items="selectedRows"
    :statuses="statusOptions"
    @close="modalClosed()" />

  <component :is="modalComponents[requestedModal]"
      v-if="requestedModal"
      ref="activeModal"
      :item-id="selectedItem && selectedItem.id"
      :match-id="match.id"
      :checklist-application-id="activeChecklistId"
      :data="selectedItem"
      @close="modalClosed()" />

</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import TextArea from 'primevue/textarea';
import Select from 'primevue/select';
// @ts-ignore
import { SaveStates } from "@/src/enums";
import { useSavableState } from '@/src/composables/useSavableState';
// @ts-ignore
import { useChecklistStore } from '@/src/stores/checklist';
// @ts-ignore
import { useAppCoreStore } from "@/src/stores/app-core";
// @ts-ignore
import { formatDateTimeET } from "@/src/date-helpers";
// @ts-ignore
import MultiSelectInput from "@/src/components/shared/FormComponents/MultiSelectInput.vue";
// @ts-ignore
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
// @ts-ignore
import DismissableMessage from "@/src/components/shared/DismissableMessage.vue";
import VerificationModal from "@/src/components/buckeye-coordinator-area/match/checklist/VerificationModal.vue";
import BulkUpdateModal from "@/src/components/buckeye-coordinator-area/match/checklist/modals/BulkUpdateModal.vue";
// @ts-ignore
import SignOffModal from '@/src/components/buckeye-coordinator-area/match/checklist/SignOffModal.vue';
import { AppliedTemplate } from "@/src/models/match/checklist/applied-template";
import { Match } from "@/src/models/match";
import { AppliedItem } from "@/src/models/match/checklist/applied-item";
import { ChecklistApplication } from "@/src/models/match/checklist/checklist-application";

const ActionModals = {
  SIGNOFF: 'sign-off-modal',
  AOC_SIGNOFF: 'verification-modal',
  BULK_UPDATE: 'bulk-update-modal'
};

// <component :is> can't resolve locally imported components by string name in <script setup>
const modalComponents: Record<string, any> = {
  [ActionModals.SIGNOFF]: SignOffModal,
  [ActionModals.AOC_SIGNOFF]: VerificationModal,
};

const props = defineProps({
  match: { required: true, type: Object },
  filters: { required: true, type: Object }
});

const router = useRouter();
const checklistStore = useChecklistStore();
const appStore = useAppCoreStore();

const {
  formError,
  currentState,
  isSavableStateProcessing,
  setSavableStateProcessing,
  setSavableStateIdle,
  setSavableStateSuccess,
  handleMajorError,
  handleFormError,
} = useSavableState();

// Template refs
const checklistItemsIndex = ref<any>(null);
const activeModal = ref<any>(null);

// Component state
const activeTab = ref(0);
const datatableKey = ref('checklist_items_index');
const selectedTemplate = ref<any>(null);
const selectedItem = ref<any>(null);
const requestedModal = ref<string | null>(null);
const errorMessage = ref<any>(null);
const selectedMatch = ref<any>(null);
const appliedTemplates = ref<Array<AppliedTemplate>>([]);
const bulkEdit = ref(false);
const selectedRows = ref<any>([]);

const isAdminVerified = computed<boolean>(() => {
  const activeChecklist = selectedMatch.value?.checklists?.[activeTab.value];
  return !!(activeChecklist?.verified_at?.value);
});

const activeChecklistId = computed<string | undefined>(() => {
  return selectedMatch.value?.checklists?.[activeTab.value]?.id;
});

const checklistItems = computed<Array<AppliedItem>>(() => {
  if (!appliedTemplates.value || appliedTemplates.value.length === 0) return [];
  return appliedTemplates.value
    .filter((t: AppliedTemplate) => t.checklist_application_id === activeChecklistId.value)
    .flatMap((t: AppliedTemplate) => t.applied_items);
});

const statusOptions = computed(() => AppliedItem.translateStatuses(checklistStore.itemStatuses));

const availableTabs = computed<string[]>(() => {
  if (!selectedMatch.value?.checklists) return [];
  return selectedMatch.value.checklists.map((c: ChecklistApplication) => c.checklist_name);
});

const hasCoordinatorSignOffEnabled = computed(() => {
  return appStore.applicationSettings.checklist.checklist_coordinator_sign_off;
});

watch(() => props.filters, () => {
  getRowData();
}, { deep: true, immediate: true });

watch(activeTab, () => {
  checklistStore.loadChecklistTemplatesAvailable(props.match.id, activeChecklistId.value);
});

onMounted(async () => {
  selectedMatch.value = props.match;
  await checklistStore.loadChecklistTemplatesAvailable(props.match.id, activeChecklistId.value);
});

function showModalFor(modalType: string, data?: any) {
  selectedItem.value = data;
  requestedModal.value = modalType;
  nextTick(() => {
    const modal = activeModal.value;
    if (!modal) {
      alert(`No modal found for ${modalType}`);
      return;
    }
    modal.showModal();
  });
}

function clearSelectedRows() {
  selectedRows.value = [];
}

function showBulkUpdateModal() {
  ($ as any)('#bulk-update-modal').modal();
}

function selectedChecklistRows(data: any) {
  selectedRows.value = data;
}

function enableBulkEdit() {
  bulkEdit.value = !bulkEdit.value;
  selectedRows.value = [];
  const dt = checklistItemsIndex.value;
  nextTick(() => {
    if (bulkEdit.value && dt.d_columnOrder) {
      const key = 'bulk_edit'; // muli select column key
      const order = dt.d_columnOrder;

      const index = order.indexOf(key);
      if (index > 0) {
        order.splice(index, 1);
        order.unshift(key);
      } else if (index === -1) {
        order.unshift(key);
      }
      dt.d_columnOrder = order;
    }
  });
}

async function modalClosed() {
  selectedRows.value = [];
  reload();
  getRowData();
}

async function reload() {
  errorMessage.value = null;
  try {
    setSavableStateProcessing();
    const response = await checklistStore.loadMatch(props.match.id);
    selectedMatch.value = Match.buildFromAPI(response);
    setSavableStateIdle();
  } catch (error) {
    handleMajorError(error);
  }
}

async function getRowData() {
  try {
    setSavableStateProcessing();
    const params = {
      match_id: props.match.id,
      template_ids: props.filters ? props.filters.template_ids : '',
      item_type_ids: props.filters ? props.filters.item_type_ids : '',
      status_ids: props.filters ? props.filters.status_ids : '',
    };
    const response = await checklistStore.loadMatchAppliedTemplates(params);
    appliedTemplates.value = response.data.map((row: any) => AppliedTemplate.buildFromAPI(row));
    if (activeTab.value >= availableTabs.value.length) {
      activeTab.value = 0;
    }
    setSavableStateIdle();
  } catch (error) {
    // TODO: Move this to a majorError and manage it better. Right now we will show this error beside the add template form which is not
    // ideal
    handleFormError(error);
    console.log(error);
  }
}

async function onCancel(event: any) {
  const row: AppliedItem = event.data;
  row.resetChanges();
}

async function onSave(data: any) {
  try {
    setSavableStateProcessing();
    const payload = {
      item_body_instance: data.item_body_instance,
      checklist_template_item_id: data.id,
      checklist_template_id: data.template_id,
      applied_item_id: data.id,
      applied_template_id: data.applied_template_id,
      status: data.status,
      match_id: props.match.id
    };

    await checklistStore.updateTemplateItemForMatch(payload);

    setSavableStateSuccess();
    getRowData();
  } catch (error) {
    handleFormError(error);
  }
}

async function addTemplate() {
  try {
    const template = checklistStore.checklistTemplatesAvailable.find((t: any) => t.id == selectedTemplate.value);
    if (!confirm(`Are you sure you want to add the template (${template.template_name})?`)) return;
    setSavableStateProcessing();
    const payload = {
      match_id: props.match.id,
      checklist_template_id: selectedTemplate.value,
      checklist_application_id: activeChecklistId.value
    };
    await checklistStore.addTemplateForMatch(payload);
    setSavableStateSuccess();
    selectedTemplate.value = null;
    await checklistStore.loadChecklistTemplatesAvailable(props.match.id, activeChecklistId.value);
    getRowData();
  } catch (error) {
    handleFormError(error);
  }
}

async function undoSignOff() {
  try {
    setSavableStateProcessing();
    await checklistStore.undoSignOffForMatch(props.match.id, activeChecklistId.value);
    setSavableStateIdle();
    reload();
  } catch (error) {
    handleFormError(error);
  }
}

function openPrintPreview() {
  const url = router.resolve({
    name: 'internal-match-checklist-print',
    params: { match_id: props.match.id },
    query: { checklist_id: activeChecklistId.value }
  }).href;
  window.open(url, '_blank', `width=${screen.availWidth},height=${screen.availHeight},resizable=yes,scrollbars=yes`);
}

async function addChecklist() {
  const checklistName = prompt('Give the checklist a name');
  // When canceled, we will return
  if (checklistName === null) return;
  if (!checklistName || !checklistName.trim()) {
    alert('You must provide a name!');
    return;
  }

  try {
    setSavableStateProcessing();
    await checklistStore.startChecklistForMatch(props.match.id, checklistName);
    setSavableStateSuccess();
    reload();
    getRowData();
  } catch (error) {
    errorMessage.value = error;
    setSavableStateIdle();
  }
}

async function editChecklistName(index: number) {
  const currentChecklistName = availableTabs.value[index] ?? '';
  const checklistName = prompt('Edit checklist name:', currentChecklistName);
  if (checklistName === null) return;
  if (!checklistName.trim()) {
    alert('You must provide a name!');
    return;
  }

  try {
    setSavableStateProcessing();
    const payload = {
      checklist_name: checklistName,
      checklist_application_id: selectedMatch.value?.checklists?.[index]?.id
    };
    await checklistStore.updateChecklistForMatch(props.match.id, payload);
    setSavableStateSuccess();
    reload();
  } catch (error) {
    errorMessage.value = error;
    setSavableStateIdle();
  }
}

function resetMessage() {
  errorMessage.value = null;
}
</script>
<style scoped>
.gap-2 {
  gap: 8px;
}
:deep(.p-datatable-tbody > tr) {
  background-color: #fff;
}
:deep(.p-datatable-tbody > tr.p-row-odd) {
  background-color: #f2f2f2;
}
:deep(.p-datatable-tbody > tr.p-datatable-row-selected) {
  background-color: var(--p-datatable-row-selected-background);
}
.row-edited {
  background-color: #fffbe6 !important; /* yellow for unsaved changes */
}
.preserve-spacing {
  white-space: pre-wrap;
  display: block; 
  overflow: hidden; 
  max-height: 3rem; 
  cursor: pointer;
}
.nav-tabs {
  border-bottom: 1px solid #ddd !important;
}
.nav {
    padding-left: 0 !important;
    margin-bottom: 0 !important;
    list-style: none !important;
}
.nav>li {
    position: relative !important;
    display: block !important;
}
.nav-tabs>li {
    float: left !important;
    margin-bottom: -1px !important;
    display: flex !important;
    align-items: center !important;
}
.nav>li>a {
    position: relative;
    display: block;
    padding: 10px 15px;
}
.nav-tabs>li>a {
    margin-right: 2px;
    line-height: 1.42857143;
    border: 1px solid transparent;
    border-radius: 4px 4px 0 0;
}
.nav-tabs>li.active>a, .nav-tabs>li.active>a:focus, .nav-tabs>li.active>a:hover {
    color: #555;
    cursor: default;
    background-color: #fff;
    border: 1px solid #ddd;
    border-bottom-color: transparent;
}
.nav-tabs>li>a.tab-name,
.nav-tabs>li.active>a.tab-name:focus,
.nav-tabs>li.active>a.tab-name:hover {
    margin-right: 0;
    border-right: none;
    border-top-right-radius: 0;
}
.nav-tabs>li>a.edit-tab-name,
.nav-tabs>li.active>a.edit-tab-name:focus,
.nav-tabs>li.active>a.edit-tab-name:hover {
    border-left: none;
    border-top-left-radius: 0;
}
.nav>li>a:hover {
    text-decoration: none;
    background-color: #eee;
}
.tab-content>.tab-pane {
    padding: 15px;
    border: 1px solid #ddd;
    border-top: none;
    background-color: #fff;
}

</style>